// A product's stock history in the Orders style (docs/plans/list-fields.md): a tile per kind
// of movement, the bill it came from, pages, tabs and the list keys. And the number cards at
// the top of the product page.
import type { MovementRow, RowOf } from '@ims/data'
import {
  Card,
  EmptyState,
  IconTile,
  Kbd,
  ListFrame,
  Pagination,
  RowTitle,
  Segmented,
  SkeletonTable,
  TD,
  TH,
  TR,
  Table,
  cx,
} from '@ims/ui'
import { ArrowsLeftRight, PackageCheck, ShoppingBasket } from '@ims/ui/icons'
import { useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import type { Lookups } from '../components.tsx'
import { useLive } from '../hooks.ts'
import { isBatchMove } from '../lib/batches.ts'
import { dateTime, qty } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { HISTORY_TYPES, movementKind, type MovementKind } from '../lib/product-stats.ts'
import { SHOP_ROW_ACTIVE } from './products.tsx'

export const MOVEMENT_LABEL: Record<string, string> = {
  opening: 'Opening stock',
  receipt: 'Received',
  sale: 'Sold',
  sale_return: 'Returned',
  adjustment: 'Adjustment',
  transfer_in: 'Transfer in',
  transfer_out: 'Transfer out',
  count: 'Stock count',
  purchase_return: 'Sent back',
}

const PAGE = 25

type Tab = 'all' | keyof typeof HISTORY_TYPES

const KIND_TILE: Record<MovementKind, { area: 'sell' | 'buy' | 'neutral'; icon: ReactNode }> = {
  sale: { area: 'sell', icon: <ShoppingBasket className="size-4" /> },
  received: { area: 'buy', icon: <PackageCheck className="size-4" /> },
  adjusted: { area: 'neutral', icon: <ArrowsLeftRight className="size-4" /> },
}

/** Where a movement came from: its document's number and page, when it has one. */
interface Source {
  label: string
  to?: string
}

export function StockHistory({
  productId,
  batches,
  lookups,
  settings,
}: {
  productId: string
  /** The product's batches, when it tracks them: the list gets a Batch column. */
  batches: RowOf<'batches'>[] | null
  lookups: Lookups
  settings: Parameters<typeof dateTime>[1]
}) {
  const navigate = useNavigate()
  const [tab, setTabValue] = useState<Tab>('all')
  const [offset, setOffset] = useState(0)
  const [active, setActive] = useState<string | null>(null)
  const setTab = (t: Tab) => {
    setTabValue(t)
    setOffset(0)
  }
  const page = useLive(
    async (repo) => {
      const result = await repo.stock.movements(productId, {
        limit: PAGE,
        offset,
        types: tab === 'all' ? undefined : [...HISTORY_TYPES[tab]],
      })
      // The documents behind this page's rows (25 at most).
      const sources = new Map<string, Source>()
      for (const m of result.rows) {
        if (!m.ref_id || sources.has(m.ref_id)) continue
        if (m.ref_type === 'sales_invoice') {
          const inv = await repo.get('sales_invoices', m.ref_id)
          if (inv)
            sources.set(m.ref_id, { label: inv.number ?? 'Draft bill', to: `/invoices/${inv.id}` })
        } else if (m.ref_type === 'credit_note') {
          const note = await repo.get('credit_notes', m.ref_id)
          if (note) sources.set(m.ref_id, { label: note.number ?? 'Credit note' })
        } else if (m.ref_type === 'goods_receipt') {
          const receipt = await repo.get('goods_receipts', m.ref_id)
          if (receipt)
            sources.set(m.ref_id, {
              label: receipt.reference || 'Goods receipt',
              to: `/purchases/${receipt.id}`,
            })
        }
      }
      return { ...result, sources }
    },
    [productId, tab, offset],
    ['stock_movements', 'sales_invoices', 'credit_notes', 'goods_receipts'],
  ).data
  const rows = page?.rows ?? []
  const batchNo = (id: string | null) => (id && batches?.find((b) => b.id === id)?.batch_no) || '—'
  const sourceOf = (m: MovementRow) => (m.ref_id ? page?.sources.get(m.ref_id) : undefined)
  const list = useRef<HTMLDivElement>(null)
  const activeRow = rows.find((m) => m.id === active)
  useListKeys({
    ids: rows.map((m) => m.id),
    active,
    setActive,
    page: { offset, limit: PAGE, total: page?.total ?? 0, setOffset },
    list,
    open: () => {
      const to = activeRow && sourceOf(activeRow)?.to
      if (to) navigate(to)
    },
    keys: {},
  })

  return (
    <ListFrame ref={list}>
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 max-sm:px-1">
        <h2 className="font-semibold">Stock history</h2>
        <Segmented
          label="Which movements"
          value={tab}
          onChange={setTab}
          className="sm:ml-auto max-sm:w-full"
          options={[
            { value: 'all', label: 'All' },
            { value: 'sales', label: 'Sales' },
            { value: 'received', label: 'Received' },
            { value: 'adjusted', label: 'Adjusted' },
          ]}
        />
      </div>
      {!page ? (
        <SkeletonTable rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState title={tab === 'all' ? 'No movements yet' : 'None of these yet'} />
      ) : (
        <>
          <Table stack="cards">
            <thead>
              <tr>
                <TH>What</TH>
                {batches && <TH>Batch</TH>}
                <TH>Where</TH>
                <TH align="right">Change</TH>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const kind = KIND_TILE[movementKind(m.type)]
                const source = sourceOf(m)
                return (
                  <TR
                    key={m.id}
                    onClick={source?.to ? () => navigate(source.to!) : undefined}
                    aria-current={active === m.id ? 'true' : undefined}
                    className={cx(active === m.id && SHOP_ROW_ACTIVE)}
                  >
                    <TD className="stack-title">
                      <RowTitle
                        tile={
                          <IconTile area={kind.area} size="sm">
                            {kind.icon}
                          </IconTile>
                        }
                        title={
                          isBatchMove(m) ? (
                            // A move between batches says where it went in its own words.
                            m.notes
                          ) : (
                            <>
                              {MOVEMENT_LABEL[m.type] ?? m.type}
                              {/* An opening stock row's note is its own name again: shown once. */}
                              {m.notes && m.notes !== MOVEMENT_LABEL[m.type] && (
                                <span className="font-normal text-muted"> · {m.notes}</span>
                              )}
                            </>
                          )
                        }
                        sub={
                          <>
                            {source &&
                              (source.to ? (
                                <Link
                                  to={source.to}
                                  className="font-semibold text-primary hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {source.label}
                                </Link>
                              ) : (
                                <span className="font-semibold">{source.label}</span>
                              ))}
                            {source && ' · '}
                            {dateTime(m.occurred_at, settings)}
                          </>
                        }
                      />
                    </TD>
                    {batches && (
                      <TD className="tabular text-[13px] text-muted max-sm:hidden">
                        {batchNo(m.batch_id)}
                      </TD>
                    )}
                    <TD className="text-[13.5px] text-muted max-sm:hidden">
                      {lookups.location(m.location_id)?.name}
                    </TD>
                    <TD
                      align="right"
                      className={cx(
                        'stack-end stack-r1 tabular whitespace-nowrap font-bold',
                        m.qty_milli < 0 ? 'text-danger' : 'text-success',
                      )}
                    >
                      {m.qty_milli > 0 ? '+' : ''}
                      {qty(m.qty_milli)}
                    </TD>
                  </TR>
                )
              })}
            </tbody>
          </Table>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
            <span>Newest first. A sale or a receipt opens its bill.</span>
            <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open
            </span>
          </div>
          <Pagination offset={offset} limit={PAGE} total={page.total} onChange={setOffset} />
        </>
      )}
    </ListFrame>
  )
}

/** One of the numbers at the top of a product's page. */
export function StatCard({
  label,
  value,
  line,
  tone,
}: {
  label: string
  value: ReactNode
  line?: ReactNode
  tone?: 'danger' | 'warning'
}) {
  return (
    <Card className="p-3 sm:p-4">
      <p className="text-sm text-muted">{label}</p>
      <p
        className={cx(
          'tabular text-xl font-bold sm:text-2xl',
          tone === 'danger' && 'text-danger',
          tone === 'warning' && 'text-warning',
        )}
      >
        {value}
      </p>
      {line && <div className="mt-1 text-xs text-muted">{line}</div>}
    </Card>
  )
}
